import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import LandingPage from '@/components/LandingPage';
import AuthModal from '@/components/AuthModal';
import Dashboard from '@/components/Dashboard';
import AdminPanel from '@/components/AdminPanel';
import PaymentSuccess from '@/components/PaymentSuccess';
import PaymentFailed from '@/components/PaymentFailed';
import PaymentPending from '@/components/PaymentPending';
import PaymentOxxo from '@/components/PaymentOxxo';
import ForgotPassword from '@/components/ForgotPassword';
import ResetPassword from '@/components/ResetPassword';
import UserProfile from '@/components/UserProfile';
import VerifyEmail from '@/components/VerifyEmail';
import AcceptInvitation from '@/components/AcceptInvitation';
import EditorialLanding from '@/components/EditorialLanding';
import EditorialPublicPage from '@/components/EditorialPublicPage';
import VerifySecondaryEmail from '@/components/VerifySecondaryEmail';
import { FxProvider } from '@/context/FxContext';
import NetworkingPage from '@/components/NetworkingPage';
import InstallPWA from '@/components/InstallPWA';
import AuthorRoyalties from '@/components/AuthorRoyalties';
import PublicProfile from '@/components/PublicProfile';
import LinkTreePage from '@/components/LinkTreePage';
import ScrollToTop from '@/components/ScrollToTop';
import { Toaster } from '@/components/ui/sonner';
import { setReferralCookie } from '@/utils/cookies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Captura global de referido (?ref=) en CUALQUIER página de Connect.
  // Garantiza que el referidor se guarde aunque el visitante no entre por el landing.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim();
      if (!ref) return;
      // Cookie cross-domain (.viubux.com) + timestamp, leída por el registro
      setReferralCookie(ref);
      // localStorage con formato {code, timestamp} que consume el formulario de registro
      localStorage.setItem('viubux_referral', JSON.stringify({ code: ref, timestamp: Date.now() }));
      // Cookie real via backend (Set-Cookie del servidor, más robusta cross-domain)
      axios.get(`${API}/referral/set-cookie?ref=${encodeURIComponent(ref)}`, { withCredentials: true }).catch(() => {});
    } catch (e) {
      // no-op
    }
  }, []);

  const fetchUser = async (retryCount = 0) => {
    const maxRetries = 2;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000 // 10 second timeout
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      
      // If it's a network error or 5xx, retry
      const isNetworkError = !error.response;
      const isServerError = error.response?.status >= 500;
      
      if ((isNetworkError || isServerError) && retryCount < maxRetries) {
        console.log(`Retrying fetchUser (attempt ${retryCount + 2}/${maxRetries + 1})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchUser(retryCount + 1);
      }
      
      // If token is invalid (401/403), logout
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
      // For other errors, keep the token but clear user (allows retry on refresh)
      else if (isNetworkError || isServerError) {
        // Don't logout - just show error state, user can refresh
        console.warn('Server temporarily unavailable, keeping session');
        setUser(null);
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-100">
        <div className="text-center">
          <img 
            src="/viubux_logo_transparent.png" 
            alt="ViubuxConnect" 
            className="h-16 mx-auto mb-4 animate-pulse"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="text-xl text-gray-600">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, refreshUser: fetchUser }}>
      <FxProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/aceptar-invitacion" element={<AcceptInvitation />} />
          <Route path="/editoriales" element={<EditorialLanding />} />
          <Route path="/editorial/:slug" element={<EditorialPublicPage />} />
          <Route path="/verify-secondary-email/:token" element={<VerifySecondaryEmail />} />
          <Route
            path="/dashboard"
            element={user ? <Dashboard /> : <Navigate to="/" />}
          />
          <Route
            path="/profile"
            element={user ? <UserProfile /> : <Navigate to="/" />}
          />
          <Route
            path="/networking"
            element={user ? <NetworkingPage /> : <Navigate to="/" />}
          />
          <Route
            path="/royalties"
            element={user ? <AuthorRoyalties /> : <Navigate to="/" />}
          />
          <Route
            path="/admin"
            element={user ? <AdminPanel /> : <Navigate to="/" />}
          />
          <Route
            path="/payment-success"
            element={user ? <PaymentSuccess /> : <Navigate to="/" />}
          />
          <Route
            path="/payment-failed"
            element={user ? <PaymentFailed /> : <Navigate to="/" />}
          />
          <Route
            path="/payment-pending"
            element={user ? <PaymentPending /> : <Navigate to="/" />}
          />
          <Route
            path="/payment-oxxo"
            element={user ? <PaymentOxxo /> : <Navigate to="/" />}
          />
          {/* Ruta de login - redirige a landing con modal */}
          <Route path="/login" element={<Navigate to="/?login=true" replace />} />
          {/* Link Tree Page - Página de enlaces del usuario (acceso directo por username) */}
          <Route path="/:username" element={<LinkTreePage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
      <AuthModal />
      <InstallPWA />
      </FxProvider>
    </AuthContext.Provider>
  );
}

export default App;